import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, TrendingDown, RefreshCw, DollarSign, ArrowRightLeft,
  ChevronDown, CheckCircle, AlertCircle, Clock, Shield, Landmark,
  ChevronRight, X, BarChart3, PieChart, ArrowDownToLine, Banknote
} from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import {
  shareConversionApi, bankApi,
  CryptoPosition, PortfolioSummary, PriceData
} from '../services/api';
import { TENANT } from '../config';
import toast from 'react-hot-toast';
import { PortalContainer } from '../components/portal/PortalContainer';

// ─── Types ───────────────────────────────────────────────
interface BankAccount {
  id: string;
  institution_name: string;
  account_name: string;
  account_type: string;
  last4: string;
  is_active: boolean;
  increase_external_account_id?: string;
}

// ─── Constants ───────────────────────────────────────────
const SUPPORTED_ASSETS = [
  { id: 'BTC', name: 'Bitcoin', icon: '₿' },
  { id: 'ETH', name: 'Ethereum', icon: 'Ξ' },
  { id: 'XRP', name: 'XRP', icon: '✕' },
  { id: 'USDC', name: 'USD Coin', icon: '$' },
];

const STATUS_CONFIG: Record<string, { label: string; classes: string; icon: React.ElementType }> = {
  active: { label: 'Active', classes: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  cashing_out: { label: 'Cashing Out', classes: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  settled: { label: 'Settled', classes: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: CheckCircle },
  cancelled: { label: 'Cancelled', classes: 'bg-muted text-muted-foreground border-border', icon: AlertCircle },
};

const PRESET_AMOUNTS = [100, 250, 500, 1000, 2500, 5000];

// ─── Utility ─────────────────────────────────────────────
const formatUsd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const formatCrypto = (n: number, asset: string) => {
  const decimals = asset === 'BTC' ? 8 : asset === 'ETH' ? 6 : 4;
  return n.toFixed(decimals);
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

// ─── Main Page ───────────────────────────────────────────
export default function ShareConversion() {
  const { user } = useUser();

  // Data
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [positions, setPositions] = useState<CryptoPosition[]>([]);
  const [history, setHistory] = useState<CryptoPosition[]>([]);
  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);

  // UI
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'convert' | 'positions' | 'liquidation' | 'history'>('overview');
  const [refreshing, setRefreshing] = useState(false);

  // Convert form
  const [selectedAsset, setSelectedAsset] = useState('BTC');
  const [amountUsd, setAmountUsd] = useState('');
  const [converting, setConverting] = useState(false);
  const [convertibleValue, setConvertibleValue] = useState<any>(null);

  // Cashout
  const [cashoutId, setCashoutId] = useState<string | null>(null);
  const [cashoutBank, setCashoutBank] = useState('');
  const [cashingOut, setCashingOut] = useState(false);

  // Liquidation
  const [liquidationRequests, setLiquidationRequests] = useState<any[]>([]);

  // ─── Data Loading ──────────────────────────────
  const loadData = useCallback(async () => {
    try {
      const [portfolioRes, positionsRes, pricesRes, historyRes, bankRes, cvRes, liqRes] = await Promise.all([
        shareConversionApi.getPortfolio(),
        shareConversionApi.getPositions(),
        shareConversionApi.getPrices(),
        shareConversionApi.getHistory({ limit: 30 }),
        bankApi.getAccounts().then(r => r.data).catch(() => ({ success: false, data: { accounts: [] } })),
        shareConversionApi.getConvertibleValue().catch(() => ({ success: false, data: null })),
        shareConversionApi.getLiquidationRequests().catch(() => ({ success: false, data: [] })),
      ]);

      if (portfolioRes.success && portfolioRes.data) setPortfolio(portfolioRes.data);
      if (positionsRes.success && positionsRes.data) setPositions(positionsRes.data);
      if (pricesRes.success && pricesRes.data) setPriceData(pricesRes.data);
      if (historyRes.success && historyRes.data) setHistory(historyRes.data);
      if (cvRes.success && cvRes.data) setConvertibleValue(cvRes.data);
      if (liqRes.success && liqRes.data) setLiquidationRequests(liqRes.data);

      // Bank accounts
      const bankData = bankRes as any;
      if (bankData?.success && bankData.data?.accounts) {
        setBankAccounts(bankData.data.accounts.filter((a: BankAccount) => a.is_active));
      } else if (Array.isArray(bankData?.data)) {
        setBankAccounts(bankData.data.filter((a: BankAccount) => a.is_active));
      }
    } catch (err) {
      console.error('Failed to load share conversion data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const refresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // ─── Convert Handler ──────────────────────────
  const handleConvert = async () => {
    const usd = parseFloat(amountUsd);
    if (!usd || usd <= 0) return toast.error('Enter a valid amount');
    if (!selectedAsset) return toast.error('Select an asset');

    setConverting(true);
    try {
      const res = await shareConversionApi.convert({ amountUsd: usd, asset: selectedAsset as 'ETH' | 'XRP' | 'BTC' });
      if (res.success && res.data) {
        toast.success(`Converted ${formatUsd(usd)} → ${formatCrypto(res.data.cryptoAmount, selectedAsset)} ${selectedAsset}`);
        setAmountUsd('');
        loadData();
      } else {
        toast.error((res as any).error || 'Conversion failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  // ─── Cashout Handler ──────────────────────────
  const handleCashout = async () => {
    if (!cashoutId || !cashoutBank) return toast.error('Select a bank account');

    setCashingOut(true);
    try {
      const res = await shareConversionApi.cashout(cashoutId, { externalAccountId: cashoutBank });
      if (res.success) {
        toast.success('Cash-out initiated — ACH settlement in progress');
        setCashoutId(null);
        setCashoutBank('');
        loadData();
      } else {
        toast.error((res as any).error || 'Cash-out failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Cash-out failed');
    } finally {
      setCashingOut(false);
    }
  };

  // ─── Computed ──────────────────────────────────
  const currentPrice = priceData?.prices?.[selectedAsset] ?? null;
  const previewCrypto = currentPrice && amountUsd ? parseFloat(amountUsd) / currentPrice : null;

  // ─── Tabs ──────────────────────────────────────
  const TABS = [
    { key: 'overview', label: 'Overview', icon: PieChart },
    { key: 'convert', label: 'Convert', icon: ArrowRightLeft },
    { key: 'positions', label: 'Positions', icon: BarChart3 },
    { key: 'liquidation', label: 'Liquidation', icon: Banknote },
    { key: 'history', label: 'History', icon: Clock },
  ] as const;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <PortalContainer className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Share Conversion</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Convert USD to crypto — custodied 1:1 by {TENANT.branding.name}
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-muted/40 hover:bg-muted/70 border border-border rounded-lg text-sm text-foreground transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/40 p-1 rounded-xl border border-border">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === key
                ? 'bg-card text-foreground border border-border shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'overview' && (
            <OverviewTab portfolio={portfolio} priceData={priceData} positions={positions} />
          )}
          {activeTab === 'convert' && (
            <ConvertTab
              selectedAsset={selectedAsset}
              setSelectedAsset={setSelectedAsset}
              amountUsd={amountUsd}
              setAmountUsd={setAmountUsd}
              priceData={priceData}
              currentPrice={currentPrice}
              previewCrypto={previewCrypto}
              converting={converting}
              handleConvert={handleConvert}
              convertibleValue={convertibleValue}
            />
          )}
          {activeTab === 'positions' && (
            <PositionsTab
              positions={positions}
              bankAccounts={bankAccounts}
              cashoutId={cashoutId}
              setCashoutId={setCashoutId}
              cashoutBank={cashoutBank}
              setCashoutBank={setCashoutBank}
              cashingOut={cashingOut}
              handleCashout={handleCashout}
            />
          )}
          {activeTab === 'history' && <HistoryTab history={history} />}
          {activeTab === 'liquidation' && (
            <LiquidationTab
              positions={positions}
              bankAccounts={bankAccounts}
              liquidationRequests={liquidationRequests}
              onRefresh={loadData}
              convertibleValue={convertibleValue}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Disclaimer */}
      <div className="flex items-start gap-3 p-4 bg-muted/30 border border-border rounded-xl">
        <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
        <div className="text-xs text-muted-foreground leading-relaxed">
          <strong className="text-foreground">Non-Guaranteed Yield.</strong> Crypto positions fluctuate with market prices.
          {TENANT.branding.name} custodies the underlying asset 1:1 and never sells it.
          Cash-out is settled via ACH at the prevailing market rate. Past performance does not guarantee future results.
        </div>
      </div>
    </PortalContainer>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════════════════

function OverviewTab({
  portfolio,
  priceData,
  positions,
}: {
  portfolio: PortfolioSummary | null;
  priceData: PriceData | null;
  positions: CryptoPosition[];
}) {
  const s = portfolio?.summary;

  return (
    <div className="space-y-6">
      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          label="Total Invested"
          value={formatUsd(s?.totalInvested ?? 0)}
          icon={DollarSign}
          color="text-blue-400"
        />
        <SummaryCard
          label="Current Value"
          value={formatUsd(s?.totalCurrentValue ?? 0)}
          icon={BarChart3}
          color="text-emerald-400"
        />
        <SummaryCard
          label="Total P&L"
          value={`${(s?.totalPnl ?? 0) >= 0 ? '+' : ''}${formatUsd(s?.totalPnl ?? 0)}`}
          sub={`${(s?.totalPnlPercent ?? 0) >= 0 ? '+' : ''}${(s?.totalPnlPercent ?? 0).toFixed(2)}%`}
          icon={(s?.totalPnl ?? 0) >= 0 ? TrendingUp : TrendingDown}
          color={(s?.totalPnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}
        />
        <SummaryCard
          label="Active Positions"
          value={String(positions.filter(p => p.status === 'active').length)}
          icon={PieChart}
          color="text-primary"
        />
      </div>

      {/* Live Prices */}
      {priceData && (
        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Live Prices</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {SUPPORTED_ASSETS.map((a) => {
              const price = priceData.prices[a.id];
              const assetData = portfolio?.assets?.[a.id];
              return (
                <motion.div
                  key={a.id}
                  whileHover={{ scale: 1.02 }}
                  className="bg-card border border-border rounded-xl p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center text-foreground font-bold text-lg">
                        {a.icon}
                      </div>
                      <div>
                        <p className="text-foreground font-semibold">{a.name}</p>
                        <p className="text-muted-foreground text-xs">{a.id}</p>
                      </div>
                    </div>
                    <p className="text-foreground font-mono text-lg">
                      {price ? formatUsd(price) : '—'}
                    </p>
                  </div>
                  {assetData && (
                    <div className="text-xs text-muted-foreground border-t border-border pt-2 mt-2 space-y-1">
                      <div className="flex justify-between">
                        <span>Holding</span>
                        <span className="text-foreground">{formatCrypto(assetData.totalCrypto, a.id)} {a.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Value</span>
                        <span className="text-foreground">{formatUsd(assetData.currentValue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>P&L</span>
                        <span className={assetData.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                          {assetData.pnl >= 0 ? '+' : ''}{formatUsd(assetData.pnl)} ({assetData.pnlPercent.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {positions.length === 0 && (
        <div className="text-center py-12 bg-muted/30 border border-border rounded-xl">
          <ArrowRightLeft className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-foreground font-medium">No positions yet</p>
          <p className="text-muted-foreground text-sm mt-1">Convert USD to crypto to get started</p>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
  icon: Icon,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-foreground">{value}</p>
      {sub && <p className={`text-sm font-medium mt-0.5 ${color}`}>{sub}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CONVERT TAB
// ═══════════════════════════════════════════════════════════════════════════

function ConvertTab({
  selectedAsset,
  setSelectedAsset,
  amountUsd,
  setAmountUsd,
  priceData,
  currentPrice,
  previewCrypto,
  converting,
  handleConvert,
  convertibleValue,
}: {
  selectedAsset: string;
  setSelectedAsset: (a: string) => void;
  amountUsd: string;
  setAmountUsd: (v: string) => void;
  priceData: PriceData | null;
  currentPrice: number | null;
  previewCrypto: number | null;
  converting: boolean;
  handleConvert: () => void;
  convertibleValue: any;
}) {
  const availableValue = convertibleValue?.totalShareValue ?? 0;

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Available Share Value Banner */}
      <div className={`border rounded-xl p-4 ${
        availableValue > 0
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : 'bg-yellow-500/10 border-yellow-500/30'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className={`w-5 h-5 ${availableValue > 0 ? 'text-emerald-400' : 'text-yellow-400'}`} />
            <span className="text-sm text-muted-foreground">Available Share Value</span>
          </div>
          <span className={`text-lg font-bold ${availableValue > 0 ? 'text-emerald-400' : 'text-yellow-400'}`}>
            {formatUsd(availableValue)}
          </span>
        </div>
        {availableValue <= 0 && (
          <p className="text-xs text-yellow-400/80 mt-2">
            You must have funded investments to convert share value to crypto.
          </p>
        )}
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
        <h3 className="text-lg font-semibold text-foreground">Convert USD → Crypto</h3>

        {/* Asset Selector */}
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Select Asset</label>
          <div className="grid grid-cols-3 gap-2">
            {SUPPORTED_ASSETS.map((a) => (
              <button
                key={a.id}
                onClick={() => setSelectedAsset(a.id)}
                className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border transition-all ${
                  selectedAsset === a.id
                    ? 'bg-primary/20 border-primary text-foreground'
                    : 'bg-background border-border text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                }`}
              >
                <span className="text-2xl">{a.icon}</span>
                <span className="text-sm font-medium">{a.name}</span>
                <span className="text-xs opacity-75">
                  {priceData?.prices?.[a.id] ? formatUsd(priceData.prices[a.id]!) : '—'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Amount Input */}
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">USD Amount</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="number"
              value={amountUsd}
              onChange={(e) => setAmountUsd(e.target.value)}
              placeholder={`Min $${priceData?.minConversionUsd ?? 50}`}
              className="w-full bg-background border border-input rounded-xl py-3 pl-10 pr-4 text-foreground text-lg placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none transition-colors"
            />
          </div>
          {/* Preset amounts */}
          <div className="flex flex-wrap gap-2 mt-2">
            {PRESET_AMOUNTS.map((amt) => (
              <button
                key={amt}
                onClick={() => setAmountUsd(String(amt))}
                className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                  amountUsd === String(amt)
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/40 text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                }`}
              >
                ${amt.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        {previewCrypto !== null && currentPrice && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-muted/30 border border-border rounded-xl p-4 space-y-2"
          >
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">You'll receive (est.)</span>
              <span className="text-foreground font-mono font-semibold">
                {formatCrypto(previewCrypto, selectedAsset)} {selectedAsset}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Rate</span>
              <span className="text-foreground">1 {selectedAsset} = {formatUsd(currentPrice)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Custody</span>
              <span className="text-emerald-600 flex items-center gap-1">
                <Shield className="w-3.5 h-3.5" /> 1:1 Custodied
              </span>
            </div>
          </motion.div>
        )}

        {/* Convert Button */}
        <button
          onClick={handleConvert}
          disabled={converting || !amountUsd || !selectedAsset || availableValue <= 0}
          className="w-full py-3.5 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
        >
          {converting ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Converting…
            </>
          ) : (
            <>
              <ArrowRightLeft className="w-4 h-4" />
              Convert to {selectedAsset}
            </>
          )}
        </button>
      </div>

      {/* Info */}
      <div className="text-xs text-muted-foreground space-y-1 px-1">
        <p>• Minimum conversion: {formatUsd(priceData?.minConversionUsd ?? 50)}</p>
        <p>• Maximum conversion: {formatUsd(priceData?.maxConversionUsd ?? 100000)}</p>
        <p>• Platform custodies the asset — you can cash out anytime via ACH</p>
        <p>• Prices update in real-time from Coinbase spot rate</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// POSITIONS TAB
// ═══════════════════════════════════════════════════════════════════════════

function PositionsTab({
  positions,
  bankAccounts,
  cashoutId,
  setCashoutId,
  cashoutBank,
  setCashoutBank,
  cashingOut,
  handleCashout,
}: {
  positions: CryptoPosition[];
  bankAccounts: BankAccount[];
  cashoutId: string | null;
  setCashoutId: (id: string | null) => void;
  cashoutBank: string;
  setCashoutBank: (v: string) => void;
  cashingOut: boolean;
  handleCashout: () => void;
}) {
  if (positions.length === 0) {
    return (
      <div className="text-center py-16 bg-muted/30 border border-border rounded-xl">
        <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-foreground font-medium">No active positions</p>
        <p className="text-muted-foreground text-sm mt-1">Convert USD to crypto to see positions here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {positions.map((pos) => {
        const assetMeta = SUPPORTED_ASSETS.find((a) => a.id === pos.asset);
        const status = STATUS_CONFIG[pos.status] || STATUS_CONFIG.active;
        const StatusIcon = status.icon;
        const isActive = pos.status === 'active';
        const isCashoutTarget = cashoutId === pos.id;

        return (
          <motion.div
            key={pos.id}
            layout
            className="bg-card border border-border rounded-xl overflow-hidden"
          >
            <div className="p-4 sm:p-5">
              {/* Top row */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-muted border border-border flex items-center justify-center text-foreground font-bold text-lg">
                    {assetMeta?.icon ?? '?'}
                  </div>
                  <div>
                    <p className="text-foreground font-semibold">{assetMeta?.name ?? pos.asset}</p>
                    <p className="text-muted-foreground text-xs font-mono">
                      {formatCrypto(pos.cryptoAmount, pos.asset)} {pos.asset}
                    </p>
                  </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${status.classes}`}>
                  <StatusIcon className="w-3 h-3" />
                  {status.label}
                </span>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Invested</p>
                  <p className="text-foreground font-medium">{formatUsd(pos.costBasisUsd)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Current Value</p>
                  <p className="text-foreground font-medium">{pos.currentValue ? formatUsd(pos.currentValue) : '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">P&L</p>
                  <p className={`font-medium ${(pos.pnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {pos.pnl !== null ? `${pos.pnl >= 0 ? '+' : ''}${formatUsd(pos.pnl)}` : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">P&L %</p>
                  <p className={`font-medium ${(pos.pnlPercent ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {pos.pnlPercent !== null ? `${pos.pnlPercent >= 0 ? '+' : ''}${pos.pnlPercent.toFixed(2)}%` : '—'}
                  </p>
                </div>
              </div>

              {/* Entry info */}
              <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span>Entered: {formatDate(pos.createdAt)}</span>
                <span>Rate: {formatUsd(pos.costBasisRate)}/{pos.asset}</span>
              </div>

              {/* Cash-out section */}
              {isActive && (
                <div className="mt-4 border-t border-border pt-4">
                  {!isCashoutTarget ? (
                    <button
                      onClick={() => setCashoutId(pos.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-muted/40 hover:bg-muted/70 border border-border rounded-lg text-sm text-foreground transition-colors"
                    >
                      <ArrowDownToLine className="w-4 h-4" />
                      Cash Out
                    </button>
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="space-y-3"
                    >
                      <label className="text-xs text-muted-foreground block">Select Bank Account for ACH Payout</label>
                      {bankAccounts.length > 0 ? (
                        <select
                          value={cashoutBank}
                          onChange={(e) => setCashoutBank(e.target.value)}
                          className="w-full bg-background border border-input rounded-lg py-2.5 px-3 text-foreground text-sm focus:border-primary focus:outline-none"
                        >
                          <option value="">Select account…</option>
                          {bankAccounts.map((b) => (
                            <option key={b.id} value={b.increase_external_account_id || b.id}>
                              {b.institution_name} — {b.account_type} ****{b.last4}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <p className="text-xs text-destructive">No linked bank accounts. Link a bank account first.</p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={handleCashout}
                          disabled={cashingOut || !cashoutBank}
                          className="flex-1 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground font-medium rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                        >
                          {cashingOut ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Landmark className="w-3.5 h-3.5" />}
                          {cashingOut ? 'Processing…' : 'Confirm Cash-Out'}
                        </button>
                        <button
                          onClick={() => { setCashoutId(null); setCashoutBank(''); }}
                          className="px-4 py-2 bg-muted/40 hover:bg-muted/70 text-muted-foreground rounded-lg text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Cashout info for cashing_out/settled */}
              {pos.cashout && (
                <div className="mt-3 bg-muted/30 border border-border rounded-lg p-3 text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cashout Requested</span>
                    <span className="text-foreground">{formatDate(pos.cashout.requestedAt)}</span>
                  </div>
                  {pos.cashout.usd && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Payout Amount</span>
                      <span className="text-emerald-600">{formatUsd(pos.cashout.usd)}</span>
                    </div>
                  )}
                  {pos.cashout.feeUsd && pos.cashout.feeUsd > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fee</span>
                      <span className="text-foreground">{formatUsd(pos.cashout.feeUsd)}</span>
                    </div>
                  )}
                  {pos.cashout.settledAt && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Settled</span>
                      <span className="text-foreground">{formatDate(pos.cashout.settledAt)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// HISTORY TAB
// ═══════════════════════════════════════════════════════════════════════════

function HistoryTab({ history }: { history: CryptoPosition[] }) {
  if (history.length === 0) {
    return (
      <div className="text-center py-16 bg-muted/30 border border-border rounded-xl">
        <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-foreground font-medium">No conversion history</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
              <th className="py-3 px-4 text-left">Date</th>
              <th className="py-3 px-4 text-left">Asset</th>
              <th className="py-3 px-4 text-right">Invested</th>
              <th className="py-3 px-4 text-right">Amount</th>
              <th className="py-3 px-4 text-right">Rate</th>
              <th className="py-3 px-4 text-right">Current Value</th>
              <th className="py-3 px-4 text-right">P&L</th>
              <th className="py-3 px-4 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {history.map((pos) => {
              const status = STATUS_CONFIG[pos.status] || STATUS_CONFIG.active;
              return (
                <tr key={pos.id} className="border-b border-border/60 hover:bg-muted/30 transition-colors">
                  <td className="py-3 px-4 text-muted-foreground">{formatDate(pos.createdAt)}</td>
                  <td className="py-3 px-4">
                    <span className="text-foreground font-medium">{pos.asset}</span>
                  </td>
                  <td className="py-3 px-4 text-right text-foreground">{formatUsd(pos.costBasisUsd)}</td>
                  <td className="py-3 px-4 text-right text-muted-foreground font-mono text-xs">
                    {formatCrypto(pos.cryptoAmount, pos.asset)}
                  </td>
                  <td className="py-3 px-4 text-right text-muted-foreground">{formatUsd(pos.costBasisRate)}</td>
                  <td className="py-3 px-4 text-right text-foreground">
                    {pos.currentValue !== null ? formatUsd(pos.currentValue) : '—'}
                  </td>
                  <td className={`py-3 px-4 text-right font-medium ${(pos.pnl ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {pos.pnl !== null ? `${pos.pnl >= 0 ? '+' : ''}${formatUsd(pos.pnl)}` : '—'}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${status.classes}`}>
                      {status.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LIQUIDATION TAB
// ═══════════════════════════════════════════════════════════════════════════

const LIQ_STATUS: Record<string, { label: string; classes: string }> = {
  pending: { label: 'Pending', classes: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  approved: { label: 'Approved', classes: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  ach_initiated: { label: 'ACH Initiated', classes: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
  settled: { label: 'Settled', classes: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  rejected: { label: 'Rejected', classes: 'bg-red-500/20 text-red-400 border-red-500/30' },
  cancelled: { label: 'Cancelled', classes: 'bg-muted text-muted-foreground border-border' },
};

function LiquidationTab({
  positions,
  bankAccounts,
  liquidationRequests,
  onRefresh,
  convertibleValue,
}: {
  positions: CryptoPosition[];
  bankAccounts: BankAccount[];
  liquidationRequests: any[];
  onRefresh: () => void;
  convertibleValue: any;
}) {
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [selectedSourceType, setSelectedSourceType] = useState<'crypto_position' | 'investment'>('crypto_position');
  const [selectedBank, setSelectedBank] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const activePositions = positions.filter(p => p.status === 'active');
  const investments = convertibleValue?.investments ?? [];

  const hasActiveLiq = (sourceId: string) =>
    liquidationRequests.some((r: any) =>
      r.sourceId === sourceId && ['pending', 'approved', 'ach_initiated'].includes(r.status)
    );

  const handleRequestLiquidation = async () => {
    if (!selectedSource || !selectedBank) return toast.error('Select a position and bank account');
    setRequesting(true);
    try {
      const res = await shareConversionApi.requestLiquidation(selectedSource, {
        sourceType: selectedSourceType,
        externalAccountId: selectedBank,
      });
      if (res.success) {
        toast.success('Liquidation request submitted — admin will process within 72 hours');
        setSelectedSource(null);
        setSelectedBank('');
        onRefresh();
      } else {
        toast.error((res as any).error || 'Request failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Request failed');
    } finally {
      setRequesting(false);
    }
  };

  const handleCancel = async (requestId: string) => {
    setCancelling(requestId);
    try {
      const res = await shareConversionApi.cancelLiquidation(requestId);
      if (res.success) {
        toast.success('Liquidation request cancelled');
        onRefresh();
      } else {
        toast.error((res as any).error || 'Cancel failed');
      }
    } catch (err: any) {
      toast.error(err.message || 'Cancel failed');
    } finally {
      setCancelling(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-primary/10 border border-primary/30 rounded-xl">
        <Banknote className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
        <div className="text-sm text-foreground">
          <strong>Liquidation Request</strong>
          <p className="text-muted-foreground mt-1">
            Request an ACH payout for your share value + earned yield. Admin has 72 hours to initiate the transfer.
            A 0.75% processing fee applies.
          </p>
        </div>
      </div>

      {/* Request Form */}
      <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
        <h3 className="text-lg font-semibold text-foreground">Request Liquidation</h3>

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Select Position to Liquidate</label>

          {activePositions.length === 0 && investments.length === 0 ? (
            <div className="text-center py-8 bg-muted/30 border border-border rounded-xl">
              <Banknote className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">No eligible positions available for liquidation</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activePositions.map(pos => {
                const assetMeta = SUPPORTED_ASSETS.find(a => a.id === pos.asset);
                const locked = hasActiveLiq(pos.id);
                return (
                  <button
                    key={pos.id}
                    disabled={locked}
                    onClick={() => { setSelectedSource(pos.id); setSelectedSourceType('crypto_position'); }}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left ${
                      selectedSource === pos.id
                        ? 'bg-primary/20 border-primary'
                        : locked
                          ? 'bg-muted/20 border-border opacity-60 cursor-not-allowed'
                          : 'bg-background border-border hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center text-foreground font-bold">
                        {assetMeta?.icon ?? '?'}
                      </div>
                      <div>
                        <p className="text-foreground text-sm font-medium">
                          {formatCrypto(pos.cryptoAmount, pos.asset)} {pos.asset}
                          {locked && <span className="text-yellow-400 ml-2 text-xs">(Request pending)</span>}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Cost basis: {formatUsd(pos.costBasisUsd)} • Current: {pos.currentValue ? formatUsd(pos.currentValue) : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-foreground font-medium text-sm">{pos.currentValue ? formatUsd(pos.currentValue) : formatUsd(pos.costBasisUsd)}</p>
                      {pos.pnl !== null && (
                        <p className={`text-xs ${pos.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {pos.pnl >= 0 ? '+' : ''}{formatUsd(pos.pnl)} yield
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}

              {investments.map((inv: any) => {
                const locked = hasActiveLiq(inv.id);
                return (
                  <button
                    key={inv.id}
                    disabled={locked}
                    onClick={() => { setSelectedSource(inv.id); setSelectedSourceType('investment'); }}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left ${
                      selectedSource === inv.id
                        ? 'bg-primary/20 border-primary'
                        : locked
                          ? 'bg-muted/20 border-border opacity-60 cursor-not-allowed'
                          : 'bg-background border-border hover:bg-muted/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center text-foreground font-bold text-sm">
                        $
                      </div>
                      <div>
                        <p className="text-foreground text-sm font-medium">
                          {inv.dealTitle || 'Investment'}
                          {locked && <span className="text-yellow-400 ml-2 text-xs">(Request pending)</span>}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {inv.sharesCount ? `${inv.sharesCount} shares • ` : ''}{formatUsd(inv.amountUsd)}
                        </p>
                      </div>
                    </div>
                    <p className="text-foreground font-medium text-sm">{formatUsd(inv.currentValue)}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {selectedSource && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-3"
          >
            <label className="text-xs text-muted-foreground uppercase tracking-wider block">Select Bank Account for ACH Payout</label>
            {bankAccounts.length > 0 ? (
              <select
                value={selectedBank}
                onChange={(e) => setSelectedBank(e.target.value)}
                className="w-full bg-background border border-input rounded-lg py-2.5 px-3 text-foreground text-sm focus:border-primary focus:outline-none"
              >
                <option value="">Select account…</option>
                {bankAccounts.map((b) => (
                  <option key={b.id} value={b.increase_external_account_id || b.id}>
                    {b.institution_name} — {b.account_type} ****{b.last4}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-destructive">No linked bank accounts. Link a bank account first.</p>
            )}

            {selectedBank && (
              <div className="bg-muted/30 border border-border rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Processing Fee</span>
                  <span className="text-foreground">0.75%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SLA</span>
                  <span className="text-foreground">ACH within 72 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Settlement</span>
                  <span className="text-foreground">1–3 business days after initiation</span>
                </div>
              </div>
            )}

            <button
              onClick={handleRequestLiquidation}
              disabled={requesting || !selectedBank}
              className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {requesting ? (
                <><RefreshCw className="w-4 h-4 animate-spin" /> Submitting…</>
              ) : (
                <><Banknote className="w-4 h-4" /> Request Liquidation</>
              )}
            </button>
          </motion.div>
        )}
      </div>

      {/* Existing Requests */}
      {liquidationRequests.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Your Liquidation Requests</h3>
          {liquidationRequests.map((req: any) => {
            const st = LIQ_STATUS[req.status] || LIQ_STATUS.pending;
            const deadline = req.deadlineAt ? new Date(req.deadlineAt) : null;
            const overdue = deadline && new Date() > deadline && req.status === 'pending';

            return (
              <div key={req.id} className="bg-card border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground font-medium text-sm">{req.requestId}</p>
                    <p className="text-muted-foreground text-xs">
                      {req.sourceType === 'crypto_position' ? `Crypto Position (${req.asset})` : 'Investment'} — {formatDate(req.createdAt)}
                    </p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${st.classes}`}>
                    {st.label}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Share Value</p>
                    <p className="text-foreground font-medium">{formatUsd(req.shareValueUsd)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Yield</p>
                    <p className="text-emerald-400 font-medium">+{formatUsd(req.yieldEarnedUsd)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Total Payout</p>
                    <p className="text-foreground font-bold">{formatUsd(req.totalPayoutUsd)}</p>
                  </div>
                </div>

                {req.feeUsd > 0 && (
                  <p className="text-xs text-muted-foreground">Fee: {formatUsd(req.feeUsd)}</p>
                )}

                {deadline && req.status === 'pending' && (
                  <div className={`flex items-center gap-2 text-xs ${overdue ? 'text-red-400' : 'text-muted-foreground'}`}>
                    <Clock className="w-3.5 h-3.5" />
                    {overdue ? 'Overdue — admin should process soon' : `Deadline: ${formatDate(req.deadlineAt)}`}
                  </div>
                )}

                {req.adminNotes && (
                  <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-lg">
                    <strong>Admin note:</strong> {req.adminNotes}
                  </p>
                )}

                {req.status === 'pending' && (
                  <button
                    onClick={() => handleCancel(req.id)}
                    disabled={cancelling === req.id}
                    className="flex items-center gap-1.5 text-xs text-destructive hover:text-destructive/80 transition-colors"
                  >
                    {cancelling === req.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                    Cancel Request
                  </button>
                )}

                {req.status === 'settled' && req.settledAt && (
                  <div className="flex items-center gap-2 text-xs text-emerald-400">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Settled on {formatDate(req.settledAt)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
